/**
 * @fileoverview Centralized logging module for corvus-web.
 *
 * Provides structured JSON logging compatible with the backend's .NET ILogger format,
 * enabling unified log aggregation across frontend and backend services.
 *
 * @example
 * ```typescript
 * import { logger, createLogger } from '@ambr/core/src/logger'
 *
 * // Use the default singleton logger
 * logger.info('Application started', { version: '1.0.0' })
 * logger.error('Failed to load data', { error: err, endpoint: '/api/users' })
 *
 * // Create a named child logger for a specific module
 * const authLogger = createLogger('auth')
 * authLogger.debug('Token refresh initiated')
 * authLogger.warn('Session expiring soon', { expiresIn: 300 })
 * ```
 */

import type { Logger, LogLevel, LogEntry, LoggerOptions, Environment } from './types'
import { LogLevelValues, EnvironmentLogLevels } from './types'
import { createBrowserTransport } from './browserTransport'
import pino, { type Logger as PinoLogger } from 'pino'

export type { Logger, LogLevel, LogEntry, LoggerOptions, Environment }
export { LogLevelValues, EnvironmentLogLevels }
export * from './testUtils'
export * from './consoleDiagnostics'

/**
 * Detects the current environment from Vite's import.meta.env.
 * Falls back to 'dev' if not detectable.
 */
export const detectEnvironment = (): Environment => {
  try {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env

    const viteMode = env?.VITE_MODE
    if (viteMode && viteMode in EnvironmentLogLevels) {
      return viteMode as Environment
    }

    const viteStandardMode = env?.MODE
    if (viteStandardMode && viteStandardMode in EnvironmentLogLevels) {
      return viteStandardMode as Environment
    }
  } catch {
    // Environment detection failed, use default
  }
  return 'dev'
}

const getDefaultLogLevel = (): LogLevel => {
  const env = detectEnvironment()
  return EnvironmentLogLevels[env] ?? 'debug'
}

/**
 * Determines if JSON output should be enabled based on environment.
 * JSON output is enabled in production/staging for log aggregation.
 */
const shouldUseJsonOutput = (): boolean => {
  const env = detectEnvironment()
  return env === 'prod' || env === 'production' || env === 'staging' || env === 'stage'
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const levelFromPino = (level: unknown): LogLevel => {
  if (typeof level === 'string') {
    if (
      level === 'trace' ||
      level === 'debug' ||
      level === 'info' ||
      level === 'warn' ||
      level === 'error' ||
      level === 'fatal'
    ) {
      return level
    }
  }

  if (typeof level === 'number') {
    if (level <= LogLevelValues.trace) {
      return 'trace'
    }
    if (level <= LogLevelValues.debug) {
      return 'debug'
    }
    if (level <= LogLevelValues.info) {
      return 'info'
    }
    if (level <= LogLevelValues.warn) {
      return 'warn'
    }
    if (level <= LogLevelValues.error) {
      return 'error'
    }
    return 'fatal'
  }

  return 'info'
}

const normalizePinoObjectToLogEntry = (pinoObject: unknown): LogEntry => {
  if (!isRecord(pinoObject)) {
    const entry: LogEntry = {
      level: 'info',
      time: Date.now(),
      msg: String(pinoObject),
    }
    return entry
  }

  const level = levelFromPino(pinoObject.level)
  const time = typeof pinoObject.time === 'number' ? pinoObject.time : Date.now()
  const msg = typeof pinoObject.msg === 'string' ? pinoObject.msg : String(pinoObject.msg)

  const { level: _level, time: _time, msg: _msg, ...rest } = pinoObject

  const entry: LogEntry = {
    level,
    time,
    msg,
    ...rest,
  }

  return entry
}

/**
 * Cache of Pino root loggers, keyed by output mode. Root loggers are shared so that
 * module loggers and child loggers can be created via `pino.child()` without
 * re-instantiating transports.
 */
const rootPinoCache = new Map<string, PinoLogger>()

const getRootPinoLogger = (options: { jsonOutput: boolean }): PinoLogger => {
  const cacheKey = options.jsonOutput ? 'json' : 'pretty'

  const cached = rootPinoCache.get(cacheKey)
  if (cached) {
    return cached
  }

  // Root transport is configured with the most permissive min level so that runtime
  // `setLevel()` changes (and per-child levels) are respected without recreating transport.
  const transport = createBrowserTransport({ jsonOutput: options.jsonOutput, level: 'trace' })

  const root = pino({
    level: 'trace',
    base: null,
    messageKey: 'msg',
    formatters: {
      bindings: (bindings: Record<string, unknown>) => bindings,
      level: (label: string) => ({ level: label }),
      log: (obj: Record<string, unknown>): Record<string, unknown> => obj,
    },
    browser: {
      asObject: true,
      write: (o: unknown): void => {
        const entry = normalizePinoObjectToLogEntry(o)
        transport(entry)
      },
    },
  })

  rootPinoCache.set(cacheKey, root)
  return root
}

const createLoggerBindings = (options: LoggerOptions): Record<string, unknown> => {
  const name = options.name
  const defaultContext = options.defaultContext ?? {}

  // Keep backwards-compatible semantics: `name` maps to `context` in the outgoing LogEntry.
  // With Pino child loggers, bindings are merged into the emitted object and normalized into LogEntry.
  if (name) {
    return { ...defaultContext, context: name }
  }

  return { ...defaultContext }
}

const stripContextOverride = (
  fixedContext: string | undefined,
  data: Record<string, unknown>
): Record<string, unknown> => {
  if (!fixedContext || !('context' in data)) {
    return data
  }

  const { context: _context, ...rest } = data
  return rest
}

/**
 * Internal logger implementation.
 */
class LoggerImpl implements Logger {
  private _level: LogLevel
  private _fixedContext?: string
  private _pino: PinoLogger

  constructor(
    options: LoggerOptions = {},
    internal?: { pino: PinoLogger; level: LogLevel; fixedContext?: string }
  ) {
    if (internal) {
      this._level = internal.level
      this._fixedContext = internal.fixedContext
      this._pino = internal.pino
      this._pino.level = internal.level
      return
    }

    this._level = options.level ?? getDefaultLogLevel()
    this._fixedContext = options.name
    const jsonOutput = options.jsonOutput ?? shouldUseJsonOutput()
    const root = getRootPinoLogger({ jsonOutput })
    const bindings = createLoggerBindings(options)

    // Always create a child logger so this instance can safely have its own level.
    this._pino = root.child(bindings)
    this._pino.level = this._level
  }

  get level(): LogLevel {
    return this._level
  }

  setLevel(level: LogLevel): void {
    this._level = level
    this._pino.level = level
  }

  private log(level: LogLevel, msg: string, data?: Record<string, unknown>): void {
    // Check if this level should be logged
    if (LogLevelValues[level] < LogLevelValues[this._level]) {
      return
    }

    // We intentionally keep two separate switch blocks here because Pino's log methods have
    // different overload shapes depending on whether you include structured data:
    // - logger.<level>(data, msg) when `data` exists
    // - logger.<level>(msg) when `data` is omitted
    // Splitting the branches keeps typing precise without unsafe index/cast access like `logger[level]`.
    if (data) {
      // Backwards-compatible behavior: when a logger has a fixed `context` (created via `createLogger('name')`
      // or `createLogger({ name })`), prevent call-site data from overriding it.
      const sanitizedData = stripContextOverride(this._fixedContext, data)

      switch (level) {
        case 'trace':
          this._pino.trace(sanitizedData, msg)
          return
        case 'debug':
          this._pino.debug(sanitizedData, msg)
          return
        case 'info':
          this._pino.info(sanitizedData, msg)
          return
        case 'warn':
          this._pino.warn(sanitizedData, msg)
          return
        case 'error':
          this._pino.error(sanitizedData, msg)
          return
        case 'fatal':
          this._pino.fatal(sanitizedData, msg)
          return
      }
      return
    }

    switch (level) {
      case 'trace':
        this._pino.trace(msg)
        return
      case 'debug':
        this._pino.debug(msg)
        return
      case 'info':
        this._pino.info(msg)
        return
      case 'warn':
        this._pino.warn(msg)
        return
      case 'error':
        this._pino.error(msg)
        return
      case 'fatal':
        this._pino.fatal(msg)
        return
    }
  }

  trace(msg: string, data?: Record<string, unknown>): void {
    this.log('trace', msg, data)
  }

  debug(msg: string, data?: Record<string, unknown>): void {
    this.log('debug', msg, data)
  }

  info(msg: string, data?: Record<string, unknown>): void {
    this.log('info', msg, data)
  }

  warn(msg: string, data?: Record<string, unknown>): void {
    this.log('warn', msg, data)
  }

  error(msg: string, data?: Record<string, unknown>): void {
    this.log('error', msg, data)
  }

  fatal(msg: string, data?: Record<string, unknown>): void {
    this.log('fatal', msg, data)
  }

  child(bindings: Record<string, unknown>): Logger {
    const childBindings = stripContextOverride(this._fixedContext, bindings)
    const childPino = this._pino.child(childBindings)

    // Wrap the Pino child logger without re-instantiating transport/config.
    return new LoggerImpl(undefined, {
      pino: childPino,
      level: this._level,
      fixedContext: this._fixedContext,
    })
  }
}

/**
 * Cache of named loggers for reuse.
 */
const loggerCache = new Map<string, Logger>()

/**
 * Creates a new logger instance with the specified options or name.
 *
 * @param nameOrOptions - Either a string name for the logger context, or full options
 * @returns A configured Logger instance
 *
 * @example
 * ```typescript
 * // Create a named logger
 * const apiLogger = createLogger('api')
 * apiLogger.info('Request started', { method: 'GET', path: '/users' })
 *
 * // Create with full options
 * const customLogger = createLogger({
 *   name: 'custom',
 *   level: 'debug',
 *   defaultContext: { service: 'frontend' }
 * })
 * ```
 */
export const createLogger = (nameOrOptions?: string | LoggerOptions): Logger => {
  if (typeof nameOrOptions === 'string') {
    // Check cache for named loggers
    const cached = loggerCache.get(nameOrOptions)
    if (cached) {
      return cached
    }

    const newLogger = new LoggerImpl({ name: nameOrOptions })
    loggerCache.set(nameOrOptions, newLogger)
    return newLogger
  }

  return new LoggerImpl(nameOrOptions)
}

/**
 * Default singleton logger instance.
 *
 * Use this for general application logging. For module-specific logging,
 * create a named logger using `createLogger('moduleName')`.
 *
 * @example
 * ```typescript
 * import { logger } from '@ambr/core/src/logger'
 *
 * logger.info('Application initialized')
 * logger.error('Unexpected error', { error: err })
 * ```
 */
export const logger: Logger = createLogger({ name: 'app' })

/**
 * Sets the global log level for the default logger.
 * This affects only the default `logger` instance.
 *
 * @param level - The new log level
 *
 * @example
 * ```typescript
 * import { setLogLevel } from '@ambr/core/src/logger'
 *
 * // Enable debug logging temporarily
 * setLogLevel('debug')
 * ```
 */
export const setLogLevel = (level: LogLevel): void => {
  logger.setLevel(level)
}

/**
 * Clears the logger cache. Useful for testing.
 * Calling this at runtime while existing loggers are in use can result in
 * multiple transports being active simultaneously.
 */
export const clearLoggerCache = (): void => {
  loggerCache.clear()
  rootPinoCache.clear()
}
